import i18n from 'i18n';
import { numberWithCommas } from 'utils/StringUtil';
import {
  DeviceAvailabilitySummary,
  DeviceUnitLevel
} from 'core/deviceManagement/DeviceManagement';
import { NO_VALUE } from './deviceDetail';
import { availabilityRateClassName } from './availabilityCalendar';
import { formatAvailabilityRate, formatSeconds } from './availabilityFormat';
import styles from './deviceManagement.module.scss';

type AvailabilitySummaryCardsProps = {
  summary?: DeviceAvailabilitySummary;
  unitLevel: DeviceUnitLevel;
};

const SummaryCard: React.FC<
  React.PropsWithChildren<{ label: string; testId: string }>
> = ({ label, testId, children }) => (
  <div className={styles.summaryCard}>
    <div className={styles.summaryLabel}>{label}</div>
    <div className={styles.summaryValue} data-testid={testId}>
      {children}
    </div>
  </div>
);

/**
 * 走期內的彙總。
 *
 * 沒有營業時段資料的點位不會被算進四個數字的任何一個，所以它有幾個要另外寫出來，
 * 否則分母看起來就是全部。
 *
 * 可售率的顏色與月曆格子同一組色階（見 availabilityCalendar），同一個比率在兩處
 * 不會是兩種顏色。
 */
export const AvailabilitySummaryCards: React.FC<
  AvailabilitySummaryCardsProps
> = ({ summary, unitLevel }) => {
  if (!summary) {
    return null;
  }

  /** 數不出來時寫「—」，不要寫 0 —— 那兩件事對業務的意思不一樣。 */
  const formatCount = (count: number | null) =>
    count === null ? NO_VALUE : numberWithCommas(count);

  /**
   * 這一句附註有沒有東西好說。
   *
   * 要明寫 null 與 0 兩種，不可以圖方便用真假值：那會把「數不出來」與「一個都沒有」
   * 折成同一件事，而上面那個 formatCount 才剛為了同一組欄位把兩者分開。附註消失時
   * 畫面上沒有任何線索，使用者會把彙總的分母讀成全部。
   */
  const hasCount = (count: number | null) => count !== null && count > 0;

  const isDeviceLevel = unitLevel === DeviceUnitLevel.DEVICE;
  const deliverableLabel = isDeviceLevel
    ? i18n.t('deviceManagement.labels.deliverableDevices')
    : i18n.t('deviceManagement.labels.deliverableStores');
  const missingUnitsMessage = isDeviceLevel
    ? 'deviceManagement.messages.missingBusinessHoursDevices'
    : 'deviceManagement.messages.missingBusinessHoursStores';
  const rate = summary.overallAvailabilityRate;

  return (
    <div className={styles.summaryArea}>
      <div className={styles.summaryCards}>
        <SummaryCard
          label={deliverableLabel}
          testId='summary-deliverable-units'
        >
          <span className={styles.summaryHighlight}>
            {formatCount(summary.deliverableUnitCount)}
          </span>
          <span className={styles.summaryTotal}>
            {` / ${formatCount(summary.totalUnitCount)}`}
          </span>
        </SummaryCard>
        <SummaryCard
          label={i18n.t('deviceManagement.labels.overallAvailabilityRate')}
          testId='summary-availability-rate'
        >
          {/* 後端給的是 0 到 1 的分數，畫面寫的是百分比。 */}
          {rate === null ? (
            NO_VALUE
          ) : (
            <span className={availabilityRateClassName(rate)}>
              {formatAvailabilityRate(rate)}
            </span>
          )}
        </SummaryCard>
        <SummaryCard
          label={i18n.t('deviceManagement.headers.availableSeconds')}
          testId='summary-available-seconds'
        >
          {formatSeconds(summary.totalAvailableSeconds)}
        </SummaryCard>
        <SummaryCard
          label={i18n.t('deviceManagement.headers.soldSeconds')}
          testId='summary-sold-seconds'
        >
          <span className={styles.soldValue}>
            {formatSeconds(summary.totalSoldSeconds)}
          </span>
        </SummaryCard>
      </div>
      {hasCount(summary.missingBusinessHoursUnitCount) && (
        <div className={styles.summaryNote} data-testid='summary-missing-units'>
          {i18n.t(missingUnitsMessage, {
            total: summary.missingBusinessHoursUnitCount
          })}
        </div>
      )}
      {hasCount(summary.uncomputableCampaignCount) && (
        <div
          className={styles.summaryNote}
          data-testid='summary-uncomputable-campaigns'
        >
          {i18n.t('deviceManagement.messages.uncomputableCampaigns', {
            total: summary.uncomputableCampaignCount
          })}
        </div>
      )}
    </div>
  );
};
